import React from "react";
import { Mail } from "lucide-react";

interface PreviewMessageProps {
  channel?: string;
  subject?: string;
  message?: string;
  emailBody?: string;
  richTextBody?: string;
  htmlTextBody?: string;
  footer?: string;
  signature?: string;
  emailFormat?: string;
  channelOptions?: Array<{ value: string; label: string }>;
  signatureOptions?: Array<{ value: string; label: string }>;
}

const PreviewMessage: React.FC<PreviewMessageProps> = ({
  channel = "",
  subject = "",
  message = "",
  emailBody = "",
  richTextBody = "",
  htmlTextBody = "",
  footer = "",
  signature = "",
  emailFormat = "0",
  channelOptions = [],
  signatureOptions = [],
}) => {
  // Determine channel display text
  const getChannelDisplay = () => {
    if (!channel) return "Select a channel";
    
    const selectedChannel = channelOptions.find(
      (opt) => String(opt.value) === String(channel)
    );
    return selectedChannel ? selectedChannel.label : "Unknown Channel";
  };

  // Get the appropriate message content based on channel and format
  const getMessageContent = () => {
    if (channel === "0") { // Email
      if (emailFormat === "0") return emailBody;
      if (emailFormat === "1") return richTextBody;
      if (emailFormat === "2") return htmlTextBody;
    }
    return message; // SMS
  };

  // Get signature label
  const getSignatureLabel = () => {
    if (!signature) return null;
    const sig = signatureOptions?.find(opt => opt.value === signature);
    return sig ? sig.label : null;
  };

  const channelDisplay = getChannelDisplay();
  const messageContent = getMessageContent();
  const signatureLabel = getSignatureLabel();

  return (
    <div className="border rounded-lg bg-white lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] flex flex-col">
      {/* Header - Fixed */}
      <div className="flex items-center gap-3 p-6 pb-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
          <Mail className="h-5 w-5 text-blue-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Message Preview</h2>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto flex-1 p-6 pt-4">
        {/* Channel Display */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Channel:</span>
            <span className="text-sm font-medium text-gray-900">
              {channelDisplay}
            </span>
          </div>
        </div>

        <hr className="mb-6 border-gray-200" />

        {/* Subject (only show if has value and is email) */}
        {subject && channel === "0" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <div className="text-sm text-gray-900 font-medium">{subject}</div>
          </div>
        )}

        {/* Message */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message Body
          </label>
          <div className="bg-gray-50 rounded-lg p-4 min-h-[200px]">
            {messageContent ? (
              <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                {emailFormat === "2" ? (
                  <div dangerouslySetInnerHTML={{ __html: messageContent }} />
                ) : (
                  messageContent
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-400 italic">
                Your message will appear here...
              </div>
            )}
          </div>
        </div>

        {/* Footer (only show if has value and is email) */}
        {footer && channel === "0" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Footer
            </label>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                {footer}
              </div>
            </div>
          </div>
        )}

        {/* Signature (only show if has value and is email) */}
        {signatureLabel && channel === "0" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Signature
            </label>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-900 font-medium">
                {signatureLabel}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewMessage;